import { v4 as uuidv4 } from 'uuid';

export const WorkflowActionTriggers = {
  OVERDUE_TASK: 1,
};

// export const RFS_WORKFLOW_TEMPLATE = "91B938A6-6F56-4106-9E9A-3379A60F0A2F";
export const RFS_WORKFLOW_TEMPLATE = "B2B2423F-75D7-4B6E-BD16-2DA15433C384";

export const WorkflowNotificationTypes = {
  DOCTOR:     1,
  PATIENT:    2,
  ASSIGNEE:   3,
  EXAMS:      4,
  ISSUES:     5,
  SCHEDULING: 6,
  REPORTS:    7,
  CUSTOM:     8,
  GENERAL:    9,
};

export const WorkflowResultTypes = {
  EXAM_PASS: 1,
  EXAM_FAIL: 0,
};

export const WorkflowTemplateTypes = {
  DEFAULT:  0,
  PROTOCOL: 1,
};

export const WorkflowInstanceTypes = {
  DEFAULT: 1,
  CHILD:   2,
  PROTOCOL: 3
};

export const WorkflowStepTypes = {
  STANDARD:        1,
  SCHEDULE_APP:    2,
  EXAM:            3,
  NESTED_WORKFLOW: 4,
  PROTOCOL_PARENT: 5,
};

export const WorkflowDueTypes = {
  DUE_FIXED_AFTER_WORKFLOW_START: 0,
  DUE_FROM_START_OF_STEP:         1,
  DUE_FROM_END_OF_STEP:           2,
  DUE_FROM_STEP_DUE_DATE:         3,
  DUE_FIXED_DATE:                 4,
  DUE_AFTER_PROTOCOL:             5,
};

export const WorkflowTriggerTypes = {
  STEP_COMPLETE:      0,
  EXAM_PASS:          1,
  EXAM_FAIL:          2,
  MISSED_APPOINTMENT: 3,
  UPCOMING_DUE_DATE:  4,
  DUE_DATE_CHANGED:   5,
  OVERDUE:            6,
  MANUAL:             8,
};

export const WorkflowActionTypes = {
  INSERT_WORKFLOW: 0,
  CANCEL_WORKFLOW: 1,
};

export const WorkflowStatusTypes = {
  ACTIVE:   1,
  INACTIVE: 2,
};

/*
*  WORKFLOW INSTANCE METHOD METHODS
* */

export const createWorkflowInstance = (
  workflowStore, workflowTemplate, patientRecordID) => {
  const instanceGUID = uuidv4();

  const workflowInstance = {
    GUID:             instanceGUID,
    templateGUID:     workflowTemplate.GUID,
    name:             workflowTemplate.name,
    usesBusinessDays: workflowTemplate.usesBusinessDays,
    startDate:        new Date().setHours(0, 0, 0, 0),
    patientRecordID:  patientRecordID,
  };

  workflowInstance.steps = createWorkflowInstanceSteps(workflowStore,
    workflowTemplate, workflowInstance);
  workflowStore.postInstance(workflowInstance);
};

const createChildWorkflowInstance = (
  workflowStore, templateGUID, parentInstance, createdSteps) => {
  const template = workflowStore.getWorkflowTemplateByGUID(templateGUID);
  const instanceGUID = uuidv4();
  const workflowInstance = {
    GUID:             instanceGUID,
    templateGUID:     template.GUID,
    type:             WorkflowInstanceTypes.CHILD,
    name:             template.name,
    activeStatus:     WorkflowStatusTypes.ACTIVE,
    completedDate:    null,
    usesBusinessDays: parentInstance.usesBusinessDays,
    startDate:        parentInstance.startDate,
    patientRecordID:  parentInstance.patientRecordID,
  };
  workflowInstance.steps = createWorkflowInstanceSteps(workflowStore, template, workflowInstance, createdSteps);
  return workflowInstance;
};

const createWorkflowInstanceSteps = (workflowStore, workflowTemplate, workflowInstance, createdSteps = []) => {
  if (workflowTemplate.steps && workflowTemplate.steps.length > 0) {
    const rootSteps = workflowTemplate.steps.filter(step => step.dueType ===
      WorkflowDueTypes.DUE_FIXED_AFTER_WORKFLOW_START);
    rootSteps.forEach(rootStep => {
      createWorkflowInstanceStep(workflowStore, rootStep, workflowInstance,
        createdSteps, workflowTemplate.steps);
    });
  }
  return createdSteps.filter(
    (createdStep) => createdStep.instanceGUID === workflowInstance.GUID);
};

const createWorkflowInstanceStep = (workflowStore, templateStep, instance, createdSteps, templateSteps) => {
  const instanceStep = {
    GUID:              uuidv4(),
    assignedTo:        null,
    completedBy:       null,
    completedDate:     null,
    daysUntilDue:      templateStep.daysUntilDue,
    dueDate:           null,
    dueType:           templateStep.dueType,
    instanceGUID:      instance.GUID,
    name:              templateStep.name,
    nestedInstance:    null,
    naReason:          null,
    referenceStepGUID: templateStep.referenceStepGUID,
    result:            null,
    templateGUID:      templateStep.GUID,
    triggers:          templateStep.triggers,
    type:              templateStep.type,
  };

  if (!isRootStep(instanceStep)) {
    const refStep = createdSteps.find((createdStep) => createdStep.templateGUID === instanceStep.referenceStepGUID);
    instanceStep.referenceStepGUID = refStep.GUID;
  }
  if (!isParentStep(instanceStep)) {
    instanceStep.dueDate = calcInstanceStepDueDate(instanceStep, instance,
      createdSteps);
  } else {
    const nestedInstance = createChildWorkflowInstance(workflowStore, templateStep.nestedTemplateGUID, instance,
      createdSteps);
    instanceStep.nestedInstance = nestedInstance;
    instanceStep.dueDate = getLastDueDate(nestedInstance);
  }
  createdSteps.push(instanceStep);
  const childSteps = templateSteps.filter(
    (tStep) => tStep.referenceStepGUID !== null && tStep.referenceStepGUID ===
      templateStep.GUID);
  childSteps.forEach(
    childStep => createWorkflowInstanceStep(workflowStore, childStep,
      instance, createdSteps, templateSteps));

};

const getLastCompletionDate = (workflowInstance) => {
  return workflowInstance.steps.reduce((prev, current) => {
    if (prev == null) {
      return current.completedDate;
    }
    return (prev > current.completedDate ? prev : current.completedDate);
  }, null);
};

export const prepWorkflowInstance = (workflowInstance) => {
  return {
    GUID:             workflowInstance.GUID,
    templateGUID:     workflowInstance.templateGUID,
    name:             workflowInstance.name,
    type:             workflowInstance.type ?
                        workflowInstance.type :
                        WorkflowInstanceTypes.DEFAULT,
    status: workflowInstance.status,
    activeStatus:     workflowInstance.activeStatus,
    startDate:        new Date(workflowInstance.startDate).getTime(),
    patientRecordID:  workflowInstance.patientRecordID,
    completedDate:    workflowInstance.completedDate !== null ?
                        new Date(workflowInstance.completedDate).getTime() :
                        null,
    usesBusinessDays: workflowInstance.usesBusinessDays,
    steps:            workflowInstance.steps.map((step) => {
      delete step.maxDays;
      step.dueDate = new Date(step.dueDate).getTime();
      if (step.completedDate !== null) {
        step.completedDate = new Date(step.completedDate).getTime();
      }
      if (step.nestedInstance && step.nestedInstance.GUID !== null) {
        step.nestedInstance = prepWorkflowInstance(step.nestedInstance);
      }
      return step;
    }),
  };
};

export const flattenSteps = (workflowInstance) => {
  let allSteps = workflowInstance.steps.map((step) => step);
  allSteps.forEach((step) => {
    if (step.nestedInstance && step.nestedInstance.steps.length > 0) {
      allSteps = allSteps.concat(flattenSteps(step.nestedInstance));
    }
  });
  return allSteps;
};

export const flattenTemplateSteps = (workflowStore, workflowTemplate) => {
  let allSteps = JSON.parse(JSON.stringify(workflowTemplate.steps));
  workflowTemplate.steps.forEach((step) => {
    let nestedTemplate;
    if (step.type === WorkflowStepTypes.NESTED_WORKFLOW) {
      nestedTemplate = workflowStore.getWorkflowTemplateByGUID(step.nestedTemplateGUID);
    } else if (step.type === WorkflowStepTypes.PROTOCOL_PARENT) {
      nestedTemplate = workflowStore.getProtocolMapByGUID(step.nestedTemplateGUID);
    } else {
      return;
    }
    allSteps = allSteps.concat(flattenTemplateSteps(workflowStore, nestedTemplate));
  });
  return allSteps;
};

export const unCompleteWorkflowInstanceStep = async (
  workflowStore, workflowInstance, step) => {
  step.completedDate = null;
  step.result = null;
  await saveWorkflowInstanceStep(workflowStore, step, workflowInstance);
};

export const completeWorkflowInstanceStep = async (
  workflowStore, workflowInstance, step) => {
  let stepTriggers = [];
  if (step.triggers.length > 0) {
    stepTriggers = workflowStore.getTriggers.filter((trigger) => {
      step.triggers.some((t) => t === trigger.GUID);
    });
  }
  stepTriggers.forEach((stepTrigger) => {
    if (stepTrigger.type === WorkflowTriggerTypes.STEP_COMPLETE) {
      activateWorkflowTrigger(workflowStore, stepTrigger, step);
    } else if (step.result === WorkflowResultTypes.EXAM_PASS &&
      stepTrigger.type === WorkflowTriggerTypes.EXAM_PASS) {
      activateWorkflowTrigger(workflowStore, stepTrigger, step);
    } else if (step.result === WorkflowResultTypes.EXAM_FAIL &&
      stepTrigger.type === WorkflowTriggerTypes.EXAM_FAIL) {
      activateWorkflowTrigger(workflowStore, stepTrigger, step);
    }
  });

  step.completedDate = new Date().getTime();
  await saveWorkflowInstanceStep(workflowStore, step, workflowInstance);
};

export const saveWorkflowInstanceStep = async (
  workflowStore, step, workflowInstance) => {
  if (flattenSteps(workflowInstance).some(s => s.GUID === step.GUID)) {
    await updateWorkflowInstanceStep(workflowStore, step, workflowInstance);
  } else {
    insertWorkflowInstanceStep(workflowStore, step, workflowInstance);
  }
};

export const updateWorkflowInstance = (workflowInstance) => {
  // In case I'm dumb and pass a reference
  let instance = JSON.parse(JSON.stringify(workflowInstance));
  instance = calcInstanceComplete(instance);
  updateWorkflowInstanceDueDates(instance);
  instance.steps.sort(sortSteps);
  return instance;
};

export const getLastDueDate = (workflowInstance) => {
  return workflowInstance.steps.reduce((prev, current) => {
    if (current.completedDate !== null) {
      return prev;
    }
    if (prev == null) {
      return current.dueDate;
    }
    return (prev > current.dueDate ? prev : current.dueDate);
  }, null);
};

const replaceWorkflowInstanceStep = (instanceStep, workflowInstance) => {
  // If the instanceStep is a direct child of this instance
  if (instanceStep.instanceGUID === workflowInstance.GUID) {
    // get the index of the step
    const i = workflowInstance.steps.findIndex(
      (step) => step.GUID === instanceStep.GUID);
    // replace the step
    workflowInstance.steps[i] = instanceStep;
  } else {
    // iterate through each of the direct child steps
    workflowInstance.steps.forEach(step => {
      if (step.nestedInstance) { // if the step has a nested Instance
        // recursively go through child instances
        step.nestedInstance = replaceWorkflowInstanceStep(instanceStep,
          step.nestedInstance);
      }
    });
  }
  return workflowInstance;
};
export const updateWorkflowInstanceStep = async (
  workflowStore, instanceStep, workflowInstance) => {
  replaceWorkflowInstanceStep(instanceStep, workflowInstance);
  const updatedInstance = updateWorkflowInstance(workflowInstance);
  await workflowStore.putInstance(updatedInstance);
};

const insertWorkflowInstanceStep = (
  workflowStore, step, workflowInstance, postWorkflow = true) => {
  let instance = JSON.parse(JSON.stringify(workflowInstance)); // Decouple the instance from the param
  const newStep = {
    GUID:              step.GUID,
    type:              step.type,
    dueType:           step.dueType,
    referenceStepGUID: step.referenceStepGUID,
    assignedTo:        null,
    maxDays:           null,
    daysUntilDue:      step.daysUntilDue,
    dueDate:           step.dueDate !== undefined ? step.dueDate : null,
    completedDate:     null,
    completedBy:       null,
    instanceGUID:      workflowInstance.GUID,
    naReason:          step.naReason,
    templateGUID:      null,
    name:              step.name,
    result:            null,
    triggers:          step.triggers,
  };
  if (step.type === WorkflowStepTypes.NESTED_WORKFLOW) { // if it's a nested workflow we're inserting
    newStep.nestedInstance = createChildWorkflowInstance(workflowStore, step.nestedTemplateGUID, workflowInstance, []);
  }
  instance.steps.push(newStep);
  if (postWorkflow) {
    workflowStore.putInstance(updateWorkflowInstance(instance));
  }// if we're sending it, send it
  else {
    return instance;
  } // else it must be a nested template, so return it so the parent step can return it
};

const allWorkflowStepsCompleted = (workflowInstance) => {
  return workflowInstance.steps.reduce((prev, current) => {
    if (current.completedDate === null && !current.naReason) {
      return false;
    }
    return prev;
  }, true);
};

const calcInstanceComplete = (workflowInstance) => {
  // Iterate through all steps
  workflowInstance.steps.forEach(step => {
    // if it has a nested instance
    if (step.nestedInstance) {
      // calculate if that instance is complete
      step.nestedInstance = calcInstanceComplete(step.nestedInstance);
      step.completedDate = step.nestedInstance.completedDate;
    }
  });
  // calc if this instance is complete AND it hasn't been completed yet
  if (allWorkflowStepsCompleted(workflowInstance)) {
    workflowInstance.completedDate = getLastCompletionDate(workflowInstance);
  } else {
    workflowInstance.completedDate = null; // set the completedDate to null
  }
  return workflowInstance;
};

const generateStepMap = (steps) => {
  const stepMap = {};
  steps.forEach((step) => {
    if (isRootStep(step)) {
      if (stepMap.rootSteps) {
        stepMap.rootSteps.push(step);
      } else {
        stepMap.rootSteps = [step];
      }
    } else {
      const refGUID = step.referenceStepGUID;
      if (stepMap[`${ refGUID }`]) {
        stepMap[`${ refGUID }`].push(step);
      } else {
        stepMap[`${ refGUID }`] = [step];
      }
    }
  });
  return stepMap;
};

const updateWorkflowInstanceDueDates = (workflowInstance) => {
  const stepMap = generateStepMap(workflowInstance.steps);
  stepMap.rootSteps.forEach((rootStep) => {
    updateWorkflowInstanceStepDueDate(rootStep, workflowInstance, stepMap);
  });

  workflowInstance.steps.forEach((step) => {
    if (step.type === WorkflowStepTypes.NESTED_WORKFLOW || step.type === WorkflowStepTypes.PROTOCOL_PARENT) {
      step.dueDate = getLastDueDate(step.nestedInstance);
    }
  });
};

export const isParentStep = (step) => step.type === WorkflowStepTypes.PROTOCOL_PARENT || step.type
  === WorkflowStepTypes.NESTED_WORKFLOW;
export const isRootStep = (step) => step.dueType === WorkflowDueTypes.DUE_FIXED_DATE || step.dueType
  === WorkflowDueTypes.DUE_FIXED_AFTER_WORKFLOW_START;

const updateWorkflowInstanceStepDueDate = (step, workflowInstance, stepMap, parentStep = null) => {
  let refDate;
  if (!isParentStep(step)) {
    if (step.dueType === WorkflowDueTypes.DUE_FIXED_AFTER_WORKFLOW_START) {
      refDate = workflowInstance.startDate;
    } else if (step.dueType === WorkflowDueTypes.DUE_FROM_START_OF_STEP || step.dueType === WorkflowDueTypes.DUE_FROM_STEP_DUE_DATE) {
      refDate = parentStep.dueDate;
    } else if (step.dueType === WorkflowDueTypes.DUE_FROM_END_OF_STEP) {
      if (parentStep.naReason !== null && parentStep.completedDate === null) { // If the previous step was N/A, reference the due date instead
        refDate = parentStep.dueDate;
      } else {
        refDate = parentStep.completedDate !== null ? parentStep.completedDate : parentStep.dueDate;
      }
    }

    if (step.dueType === WorkflowDueTypes.DUE_FROM_START_OF_STEP) {
      step.dueDate = addDays(refDate, step.daysUntilDue - parentStep.daysUntilDue, workflowInstance.usesBusinessDays);
    } else if (step.dueType !== WorkflowDueTypes.DUE_FIXED_DATE) {
      step.dueDate = addDays(refDate, step.daysUntilDue, workflowInstance.usesBusinessDays);
    } else { // The Due Type must be DUE_FIXED_DATE
      const tmpDate = new Date();
      tmpDate.setTime(step.dueDate);
      step.dueDate = new Date(new Date(tmpDate.getFullYear(), tmpDate.getMonth(), tmpDate.getDate()));
    }

  } else {
    const nestedInstance = step.nestedInstance;
    const childMap = generateStepMap(nestedInstance.steps);

    childMap.rootSteps.forEach(() => {
      if (parentStep != null) {
        if (step.dueType === WorkflowDueTypes.DUE_FROM_END_OF_STEP && parentStep.completedDate !== null) {
          refDate = parentStep.completedDate;
        } else {
          refDate = parentStep.dueDate;
        }
      } else if (step.dueType === WorkflowDueTypes.DUE_FIXED_DATE) {
        refDate = step.dueDate;
      } else {
        refDate = new Date();
      }
      if (step.dueType === WorkflowDueTypes.DUE_FROM_START_OF_STEP) {
        if (parentStep == null) {
          throw new Error();
        }
        refDate = addDays(refDate, -parentStep.daysUntilDue, workflowInstance.usesBusinessDays);
      }
      nestedInstance.startDate = refDate;

      childMap.rootSteps.forEach(rootStep => {
        if (isParentStep(rootStep)) {
          updateWorkflowInstanceStepDueDate(rootStep, nestedInstance, childMap, parentStep);
        } else {
          updateWorkflowInstanceStepDueDate(rootStep, nestedInstance, childMap, step);
        }
      });

      step.dueDate = getLastDueDate(nestedInstance);
    });
  }

  const childSteps = stepMap[`${ step.GUID }`];
  if (childSteps && childSteps.length > 0) {
    childSteps.forEach(childStep => updateWorkflowInstanceStepDueDate(childStep,
      workflowInstance, stepMap, step));
  }
};

const calcInstanceStepDueDate = (instanceStep, instance, createdSteps) => {
  let refDate;
  let refStep = createdSteps.find(
    (createdStep) => createdStep.GUID === instanceStep.referenceStepGUID);
  if (instanceStep.dueType ===
    WorkflowDueTypes.DUE_FIXED_AFTER_WORKFLOW_START) {
    refDate = instance.startDate;
  } else if (
    instanceStep.dueType === WorkflowDueTypes.DUE_FROM_START_OF_STEP ||
    instanceStep.dueType === WorkflowDueTypes.DUE_FROM_STEP_DUE_DATE) {
    refDate = refStep.dueDate;
  } else if (instanceStep.dueType === WorkflowDueTypes.DUE_FROM_END_OF_STEP) {
    if (refStep.completedDate !== null) { // If the step is complete
      refDate = refStep.completedDate; // set the ref to the completed date
    } else { // the step is not completed
      refDate = refStep.dueDate; // set the ref to the due date instead
    }
  }

  if (instanceStep.dueType === WorkflowDueTypes.DUE_FROM_START_OF_STEP) {
    return addDays(refDate, instanceStep.daysUntilDue - refStep.daysUntilDue,
      instance.usesBusinessDays);
  } else if (instanceStep.dueType !== WorkflowDueTypes.DUE_FIXED_DATE) {
    return addDays(refDate, instanceStep.daysUntilDue,
      instance.usesBusinessDays);
  } else { // The Due Type must be DUE_FIXED_DATE
    return new Date(instanceStep.dueDate);
  }
};

export const sortInstanceSteps = (workflowInstance) => {
  workflowInstance.steps = workflowInstance.steps.sort(sortSteps);
  return workflowInstance;
};

const sortSteps = (stepA, stepB) => {
  if (stepA.completedDate && !stepB.completedDate) {
    return -1;
  } else if (!stepA.completedDate && stepB.completedDate) {
    return 1;
  } else if (stepA.completedDate && stepB.completedDate) {
    if (stepA.completedDate - stepB.completedDate > 0) {// If A was after B
      return -1;
    } else if (stepA.completedDate === stepB.completedDate) {
      return 0;
    }
    return 1;
  } else { // They must both be incomplete
    if (stepA.dueDate - stepB.dueDate > 0) {
      return 1;
    } else if (stepA.dueDate === stepB.dueDate) {
      return 0;
    } else {
      return -1;
    }
  }
};

/*
* WORKFLOW TEMPLATES METHODS
*/

export const saveWorkflowTemplate = (workflowStore, workflowTemplate) => {
  if (workflowStore.getWorkflowTemplates.some(
    (t) => t.GUID === workflowTemplate.GUID)) {
    workflowStore.putTemplate(workflowTemplate);
  } else {
    workflowStore.postTemplate(workflowTemplate);
  }
};

export const prepWorkflowTemplate = (workflowTemplate) => {
  return {
    GUID:             workflowTemplate.GUID,
    type:             workflowTemplate.type,
    clientIDs:        workflowTemplate.clientIDs,
    usesBusinessDays: workflowTemplate.usesBusinessDays,
    activeStatus:     workflowTemplate.activeStatus,
    name:             workflowTemplate.name,
    steps:            workflowTemplate.steps.map((step) => {
      delete step.maxDueDate;
      return step;
    }),
  };
};

/*
* WORKFLOW TRIGGER METHODS
* */

export const saveWorkflowTrigger = (workflowStore, trigger) => {
  if (workflowStore.getTriggers.some((t) => t.GUID === trigger.GUID)) {
    workflowStore.putTrigger(trigger);
  } else {
    workflowStore.postTrigger(trigger);
  }
};

export const activateWorkflowTrigger = async (workflowStore, trigger, step) => {
  const stepTrigger = {
    triggerGUID:  trigger.GUID,
    instanceGUID: step.GUID,
  };
  await workflowStore.activateWorkflowTrigger(stepTrigger);
};

/*
* Protocol Methods
* */

export const saveProtocolMap = (workflowStore, protocolMap) => {
  if (workflowStore.getProtocolMaps.some(p => p.GUID === protocolMap.GUID)) {
    workflowStore.putProtocolMap(protocolMap);
  } else {
    workflowStore.postProtocolMap(protocolMap);
  }
};

export const prepProtocolMap = (protocolMap) => {
  return {
    GUID:             protocolMap.GUID,
    type:             protocolMap.type,
    name:             protocolMap.name,
    clientIDs:        protocolMap.clientIDs,
    activeStatus:     protocolMap.activeStatus,
    usesBusinessDays: protocolMap.usesBusinessDays,
    steps:            protocolMap.steps.map((step) => {
      delete step.maxDueDate;
      return step;
    }),
    protocols:        protocolMap.protocols,
  };
};

/*
* GENERAL METHODS
* */

const addDays = (date, days, usesBusinessDays) => {
  if (usesBusinessDays) {
    return addWorkDays(date, days);
  }
  const updatedDate = new Date(date);
  updatedDate.setTime(updatedDate.getTime() + (days * 24 * 60 * 60 * 1000));
  return updatedDate;
};

const addWorkDays = (startDate, daysToAdd) => {
  startDate = new Date(startDate);
  const dayOfWeek = startDate.getDay();
  // If the day of the week is a sunday
  if (dayOfWeek === 0) {
    // add a day
    daysToAdd += 1;
  }
  // If the start date + days to add lands on or after the next saturday
  if (dayOfWeek + daysToAdd >= 6) {
    // subtract days in current week from work days left to add
    const remainingWorkDays = daysToAdd - (5 - dayOfWeek);
    // add the weekend
    daysToAdd += 2;
    if (remainingWorkDays > 5) {
      // add two days for each working week left
      daysToAdd += (2 * Math.floor(remainingWorkDays / 5));
      // knock off the final weekend if we end on a friday
      if (remainingWorkDays % 5 === 0) {
        daysToAdd -= 2;
      }
    }
  }

  startDate.setTime(startDate.getTime() + (daysToAdd * 24 * 60 * 60 * 1000));
  return startDate;
};
